import { useState } from 'react'

import { useRequest } from 'ahooks'
import { Button, Flex, Modal, Skeleton } from 'antd'
import { createStyles, useTheme } from 'antd-style'

import { queryHelpDocApi } from '@eastcoal/api/content/helpDocMall/queryHelpDoc'
import { HelpDocEntity } from '@eastcoal/api/content/interface'

import { DataError } from '@eastcoal/common/components/DataError'

import { RichEditor } from '@eastcoal/editor/index'
import { RouterLink } from '@eastcoal/router/components/Link'

import type { FlowIntroduceColumnProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  lineDivider: {
    width: 1,
    height: 64,
    backgroundColor: token.colorBorder
  },
  column: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: token.paddingXS,
    placeContent: 'start center',
    [`&.has-divider::before`]: {
      content: '""',
      position: 'absolute',
      left: -token.paddingMD,
      width: 16,
      height: 16,
      clipPath: 'polygon(0 0, 100% 50%, 0 100%, 70% 50%)',
      backgroundColor: token.colorBorder
    }
  },
  catalogue: {
    display: 'flex',
    alignItems: 'center',
    columnGap: token.paddingXS,
    fontSize: token.fontSizeHeading5,
    [`&.has-index::before`]: {
      content: 'attr(data-index)',
      width: '1em',
      height: '1em',
      lineHeight: 1,
      textAlign: 'center',
      borderRadius: '50%',
      boxSizing: 'content-box',
      padding: token.paddingXXS,
      fontSize: token.fontSize,
      fontWeight: token.fontWeightStrong,
      color: token.colorWhite,
      backgroundColor: token.colorPrimary
    }
  },
  articles: {
    display: 'grid',
    rowGap: token.paddingXXS
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    columnGap: token.paddingXXS,
    color: token.colorTextTertiary,
    fontSize: token.fontSizeSM,
    [`&.has-icon::before`]: {
      content: '""',
      display: 'inline-block',
      width: 4,
      height: 8,
      clipPath: 'polygon(0 0, 0 100%, 100% 50%)',
      backgroundColor: token.colorBorder
    }
  }
}))

const DocMap = new Map<string, HelpDocEntity>()

export const FlowIntroduceColumn = ({
  index,
  catalogue,
  articles
}: FlowIntroduceColumnProps) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [articleId, setArticleId] = useState<string>()
  const { data, loading, error, refreshAsync } = useRequest(
    () =>
      queryHelpDocApi({
        id: articleId
      }),
    {
      manual: false,
      ready: open,
      refreshDeps: [open],
      getCache(params) {
        return {
          data: articleId ? DocMap.get(articleId) : undefined,
          params,
          time: -1
        }
      },
      setCache(data) {
        if (data.data && articleId) {
          DocMap.set(articleId, data.data)
        }
      }
    }
  )

  const { cx, styles } = useStyles()

  return (
    <>
      <div className={styles.column}>
        <div
          data-index={index}
          className={cx(styles.catalogue, {
            ['has-index']: index > 0
          })}
        >
          {catalogue}
        </div>
        <div className={styles.articles}>
          {articles?.map(({ id, name, url, prefixIcon }) => {
            const titleNode = (
              <div
                className={cx(styles.title, [
                  {
                    ['has-icon']: prefixIcon
                  }
                ])}
              >
                {name}
              </div>
            )
            return url ? (
              <RouterLink key={name} to={url} type='default'>
                {titleNode}
              </RouterLink>
            ) : (
              <Button
                key={name}
                size='small'
                type='text'
                onClick={() => {
                  setArticleId(id)
                  setOpen(true)
                }}
              >
                {titleNode}
              </Button>
            )
          })}
        </div>
      </div>
      <Modal
        cancelButtonProps={{ hidden: true }}
        open={open}
        title={loading ? null : <Flex justify='center'>{data?.title}</Flex>}
        width={800}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
      >
        {loading ? (
          <Skeleton active />
        ) : error ? (
          <DataError block error={error} reload={refreshAsync} />
        ) : (
          <RichEditor
            readPretty
            style={{ backgroundColor: theme.blue1 }}
            value={data?.content}
          />
        )}
      </Modal>
    </>
  )
}
